import { Component, ChangeDetectionStrategy, inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { GlobalLoaderComponent } from './core/loading/global-loader.component';
import { ZardToastComponent } from '@/shared/components/toast';
import { LoaderService } from './core/loading/loader.service';
import { APP_FAVICON } from './core/config/app-brand';
import { ImportExportLogService } from '@/features/audit/services/import-export-log.service';
import { DEFAULT_EXPORT_SERVICE, setExportAuditLogger } from '@shared/utils/export-audit';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, GlobalLoaderComponent, ZardToastComponent],
    template: `
        <app-global-loader />
        <router-outlet />
        <z-toast position="bottom-right" [duration]="3500" />
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private loaderService = inject(LoaderService);
    private document = inject(DOCUMENT);
    private importExportLog = inject(ImportExportLogService);

    ngOnInit() {
        const favicon = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (favicon) {
            favicon.href = APP_FAVICON;
        }

        this.router.events.subscribe((event) => {
            if (
                event instanceof NavigationEnd ||
                event instanceof NavigationCancel ||
                event instanceof NavigationError
            ) {
                this.loaderService.hide();
            }
        });

        // Every PDF / Excel / CSV export (shared/utils) is recorded in the Import/Export Log.
        setExportAuditLogger((payload) => {
            this.importExportLog
                .recordExport({
                    serviceName: payload.serviceName || DEFAULT_EXPORT_SERVICE,
                    moduleName: payload.moduleName ?? '',
                    fileName: payload.fileName ?? '',
                    result: payload.result || 'Success',
                    totalRecords: payload.totalRecords,
                })
                .subscribe({
                    error: () => {
                        // Audit failures are silent — the export itself already finished for the user.
                    },
                });
        });
    }

    ngOnDestroy() {
        setExportAuditLogger(null);
    }
}
